import { css } from '@emotion/react'
import { useState } from 'react'
import { inject, observer } from 'mobx-react'

import { Page, PageHeader } from '../elements'
import {
  ClientPlan,
  SpousePlan,
  AdditionalSavings,
  OtherAccounts,
  NothingTodo,
  UpdateInformation,
} from './blocks'
import { MainTitle, ContentText, FootNote, ContentContainer, TodoBox } from './styled'
import Investments from '../../../models/Investments'

const todoPageConfig = templateId => {
  switch (templateId) {
    case 1:
      return {
        clientPlanEnabled: true,
        footNote: (
          <FootNote>
            Details of savings and investment changes can be found in upcoming sections of this
            guide.
          </FootNote>
        ),
      }

    case 2:
      return {
        clientPlanEnabled: false,
        footNote: (
          <FootNote>
            Details of savings and investment changes can be found in upcoming sections of this
            guide.
          </FootNote>
        ),
      }
    case 19:
      return {
        clientPlanEnabled: true,
        footNote: null,
      }
    case 3:
    case 4:
    case 5:
    case 6:
    case 7:
    case 8:
    case 9:
    case 10:
    default:
      return {
        clientPlanEnabled: false,
        footNote: (
          <FootNote
            css={css`
              font-family: Aleo;
              font-size: 12pt;
              font-weight: bold;
            `}>
            Your plan and the income you can expect at retirement rely on you to make the changes
            you've modeled, then to return each year to update your balances and to let us know of
            any changes affecting your plan so that we can update our advice.
          </FootNote>
        ),
      }
  }
}

const TodoPage = props => {
  useState(() => {
    props.guide.guidePages.pages.addPage({
      name: 'todo',
    })
  })

  const [state] = useState(() => Investments.create({ investments: [] }))
  const [infoNeedsUpdating, setInfoNeedsUpdating] = useState(false)

  const includeClientPlan = () => {
    const { config, clientContributionChange, employerAccount } = props.store
    const { displayName, id: personId } = props.store.person
    const { riskLevel: caseRiskLevel } = props.store.acceptedCase
    const { riskLevel: baseRiskLevel } = props.store.baseCase
    const employerAcctsWithInvestmentAdvice = employerAccount.filter(acct => acct.adviced === true)
    const noAcctsWithInvestmentAdvice = (employerAcctsWithInvestmentAdvice?.length ?? 0) === 0

    if (noAcctsWithInvestmentAdvice) {
      return null
    }

    let clientEmployerAccount

    if (config.isRetail && employerAcctsWithInvestmentAdvice.length > 0) {
      clientEmployerAccount = employerAcctsWithInvestmentAdvice.filter(
        acct => acct.participantId === personId
      )[0]
    } else {
      clientEmployerAccount = employerAcctsWithInvestmentAdvice[0]
    }

    return (
      caseRiskLevel !== null &&
      baseRiskLevel !== null &&
      caseRiskLevel !== baseRiskLevel && (
        <ClientPlan
          title={`Changes to ${displayName ? `${displayName}'s` : ''} ${
            config.isRetail ? (clientEmployerAccount?.name ?? '') : config.template.planName
          } account`}
          contribution={clientContributionChange && 'Update contribution'}
          investmentMix='Update investment mix'
          additionalText={
            config.isRetail
              ? undefined
              : 'GuidedChoice sends automatically to the plan administrator, any investment mix changes when the option to implement is selected through the advisory service. Finalization of those investment changes was completed through the Investment Plan Administrator. The investment holdings and their associated allocation percentages is detailed in the next section of your guide.'
          }
        />
      )
    )
  }

  const includeSpousePlan = () => {
    const { firstName: spouseName } = props.store.spouse
    const { includedSpouseEmployerAccount, spouseContributionChange } = props.store

    return (
      includedSpouseEmployerAccount &&
      (includedSpouseEmployerAccount.adviced === true || spouseContributionChange) && (
        <SpousePlan
          title={`Changes to ${
            spouseName ? `${spouseName}'s` : ''
          } current employer plan account (${includedSpouseEmployerAccount.name})`}
          contribution={spouseContributionChange && 'Update contribution'}
          investmentMix={includedSpouseEmployerAccount.adviced === true && 'Update investment mix'}
        />
      )
    )
  }

  const includeAdditionalSavings = () => {
    const { special457, special403b, employerAccount } = props.store
    const {
      client457PreTaxSaving: base457pretax,
      client457RothSaving: base457roth,
      client403bPreTaxSaving: base403bpretax,
    } = props.store.baseCase
    const {
      outOfPlanMonthlySavings,
      client457PreTaxSaving: case457pretax,
      client457RothSaving: case457roth,
      client403bPreTaxSaving: case403bpretax,
    } = props.store.acceptedCase

    const special457update =
      employerAccount[0]?.planType !== 4 &&
      special457 &&
      (base457pretax !== case457pretax || base457roth !== case457roth)

    const special403bupdate =
      employerAccount[0]?.planType !== 2 && special403b && base403bpretax !== case403bpretax

    return (
      (outOfPlanMonthlySavings > 0 || special457update || special403bupdate) && (
        <AdditionalSavings />
      )
    )
  }

  const includeOtherAccounts = () => {
    const { otherIncludedAccountsWithoutSpouseEmployer } = props.store

    return (
      otherIncludedAccountsWithoutSpouseEmployer.length > 0 && (
        <OtherAccounts accounts={otherIncludedAccountsWithoutSpouseEmployer} />
      )
    )
  }

  const updateInformation = () => {
    const {
      store: {
        config: { isRetail },
      },
    } = props

    return isRetail ? (
      <UpdateInformation
        getInvestments={state.getInvestments}
        setInfoNeedsUpdating={setInfoNeedsUpdating}
      />
    ) : null
  }

  const page = props.guide.guidePages.pages.findByName('todo')
  const {
    config: { templateId },
  } = props.store

  const todoPage = todoPageConfig(templateId)

  const clientPlan = includeClientPlan()
  const spousePlan = includeSpousePlan()
  const additionalSavings = includeAdditionalSavings()
  const otherAccounts = includeOtherAccounts()
  const updateInformationContent = updateInformation()

  return (
    <Page header={<PageHeader />} page={page}>
      <TodoBox data-todo-box>
        <ContentContainer>
          <MainTitle>TO-DO LIST</MainTitle>

          <div
            css={css`
              padding: 10px 0;
            `}
          />

          <ContentText>
            While there are no absolute deadlines for your to-do list, this guide will get less
            accurate over time. If, like most to-do lists, it takes awhile to get around to, just
            log in again for a fresh version.
          </ContentText>

          <hr style={{ borderColor: '#038AB7', margin: '24px 0 20px' }} />

          {(todoPage.clientPlanEnabled && clientPlan) || null}

          {spousePlan || null}

          {additionalSavings || null}

          {otherAccounts || null}

          {updateInformationContent || null}

          {!(todoPage.clientPlanEnabled && clientPlan) &&
            !spousePlan &&
            !additionalSavings &&
            !otherAccounts &&
            !infoNeedsUpdating && <NothingTodo />}
        </ContentContainer>
      </TodoBox>

      {todoPage.footNote}
    </Page>
  )
}

export default inject('store', 'guide')(observer(TodoPage))
